// @ts-nocheck
import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import ToastNotifications from './components/ToastNotifications';
import ErrorBoundary from './components/ui/ErrorBoundary';
import BrandedAccountLoader from './components/BrandedAccountLoader';
import { disableAnalytics, initAnalytics, trackPageView } from './lib/analytics';
import { CurrencyProvider } from './lib/currencyContext';
import { WAITLIST_MODE } from './constants/featureFlags';
import { PRODUCTION_PRODUCT_MODE } from './lib/product-mode.ts';
import { useNearViewport } from './hooks/useNearViewport';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import {
  CITY_HOME_ROUTES,
  COMING_SOON_ROUTES,
  ROLE_HOME_ROUTES,
  SALARY_DETAIL_PATHS,
  SPECIALIZED_HOME_ROUTES,
} from './appRouteConfig';

const CookieBanner = React.lazy(() => import('./components/CookieBanner'));
const LanguageDetectBanner = React.lazy(() => import('./components/LanguageDetectBanner'));
const AuthModal = React.lazy(() => import('./components/AuthModal'));
const Home = React.lazy(() => import('./pages/Home'));
const AuthCallbackPage = React.lazy(() => import('./pages/AuthCallbackPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const AccountShell = React.lazy(() => import('./pages/AccountShell'));
const AccountLayout = React.lazy(() => import('./layouts/AccountLayout'));
const Footer = React.lazy(() => import('./components/Footer'));
const AccountSetupSection = React.lazy(() => import('./pages/account/AccountSetupSection'));
const AccountOnboardingSection = React.lazy(() => import('./pages/account/AccountOnboardingSection'));

const AccountJobsSection = React.lazy(() => import('./pages/account/AccountJobsSection'));
const AccountEventsSection = React.lazy(() => import('./pages/account/AccountEventsSection'));

const AccountCvSection = React.lazy(() => import('./pages/account/AccountCvSection'));
const AccountAgentSection = React.lazy(() => import('./features/account/chat/index.ts'));
const AccountSmartSearchSection = React.lazy(() => import('./features/account/smart-search/index.ts'));
const AccountTrackingSection = React.lazy(() => import('./pages/account/AccountTrackingSection'));
const AccountBoardSection = React.lazy(() => import('./pages/account/AccountBoardSection'));
const AccountInboxSection = React.lazy(() => import('./pages/account/AccountInboxSection'));
const AccountOverviewSection = React.lazy(() => import('./pages/account/AccountOverviewSection'));
const AccountBillingSection = React.lazy(() => import('./pages/account/AccountBillingSection'));
const AccountSwipeSection = React.lazy(() => import('./features/account/swipe-jobs/index.ts'));
const AccountSettingsSection = React.lazy(() => import('./pages/account/AccountSettingsSection'));
const AccountSavedJobsSection = React.lazy(() => import('./pages/account/AccountSavedJobsSection'));
const ProductionPreferencesSection = React.lazy(() => import('./pages/account/ProductionPreferencesSection'));
const JobDetail = React.lazy(() => import('./pages/JobDetail'));
const Advertise = React.lazy(() => import('./pages/Advertise'));
const CompanyPage = React.lazy(() => import('./pages/CompanyPage'));
const CompanySalaryPage = React.lazy(() => import('./pages/CompanySalaryPage'));
const CompaniesPage = React.lazy(() => import('./pages/CompaniesPage'));
const SalaryPage = React.lazy(() => import('./pages/SalaryPage'));
const ObservatoryPage = React.lazy(() => import('./pages/ObservatoryPage'));
const SalaryDetail = React.lazy(() => import('./pages/SalaryDetail'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
const ComingSoonPage = React.lazy(() => import('./pages/ComingSoonPage'));
const ArticlePage = React.lazy(() => import('./pages/ArticlePage'));
const TrainingPage = PRODUCTION_PRODUCT_MODE ? null : React.lazy(() => import('./pages/TrainingPage'));
const TrainingBoardPage = PRODUCTION_PRODUCT_MODE ? null : React.lazy(() => import('./pages/TrainingBoardPage'));
const ClientNedTrainingPage = PRODUCTION_PRODUCT_MODE ? null : React.lazy(() => import('./pages/ClientNedTrainingPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const CookiePolicyPage = React.lazy(() => import('./pages/CookiePolicyPage'));
const AiTransparencyPage = React.lazy(() => import('./pages/AiTransparencyPage'));
const EventDetail = React.lazy(() => import('./pages/EventDetail'));
const AlertDigestPage = React.lazy(() => import('./pages/AlertDigestPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const EventsPage = React.lazy(() => import('./pages/EventsPage'));
const JobCityLanding = React.lazy(() => import('./pages/JobCityLanding'));
const EventCityLanding = React.lazy(() => import('./pages/EventCityLanding'));
const LearnAiPage = React.lazy(() => import('./pages/LearnAiPage'));
const ToolsPage = React.lazy(() => import('./pages/ToolsPage'));
const ToolDetailPage = React.lazy(() => import('./pages/ToolDetailPage'));
const PublicProfilePage = React.lazy(() => import('./pages/PublicProfilePage'));
const PublicProfileCvPage = React.lazy(() => import('./pages/PublicProfileCvPage'));
const DesktopWorkspacePage = PRODUCTION_PRODUCT_MODE ? null : React.lazy(() => import('./pages/DesktopWorkspacePage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

const HERO_PATHS = new Set([
  '/', '/jobs', '/companies', '/events', '/learn-ai', '/ai-tools', '/observatory',
  ...CITY_HOME_ROUTES.map(r => r.path),
  ...ROLE_HOME_ROUTES.map(r => r.path),
  ...SPECIALIZED_HOME_ROUTES.map(r => r.path),
]);

const THEME_STORAGE_KEY = 'theme';
const THEME_COOKIE_KEY = 'ac_theme';
const SHARED_THEME_COOKIE_KEY = 'ac_theme_shared';

function readThemeCookie() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|; )ac_theme=([^;]+)/);
  const value = match ? decodeURIComponent(match[1]) : '';
  return value === 'dark' || value === 'light' ? value : null;
}

function readSharedThemeCookie() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|; )ac_theme_shared=([^;]+)/);
  const value = match ? decodeURIComponent(match[1]) : '';
  return value === 'dark' || value === 'light' ? value : null;
}

function persistTheme(theme) {
  localStorage.setItem(THEME_STORAGE_KEY, theme);
  document.cookie = `${THEME_COOKIE_KEY}=${encodeURIComponent(theme)}; Path=/; Max-Age=31536000; SameSite=Lax`;
  const host = window.location.hostname.toLowerCase();
  if (host === 'artificial.careers' || host.endsWith('.artificial.careers')) {
    document.cookie = `${SHARED_THEME_COOKIE_KEY}=${encodeURIComponent(theme)}; Domain=.artificial.careers; Path=/; Max-Age=31536000; SameSite=Lax; Secure`;
  }
}

function App() {
  const {t}=useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isPublicProfileRoute = location.pathname.startsWith('/my/');
  const isAccountRoute = location.pathname.startsWith('/account');
  const isSignupRoute = location.pathname === '/signup';
  const isDesktopWorkspaceRoute = location.pathname === '/desktop/workspace';
  const isTrainingStudioRoute = /^\/learn-ai\/training\/[^/]+\/[^/]+(?:\/[^/]+)?\/?$/.test(location.pathname);
  const isEmbedded = new URLSearchParams(location.search).has('embed');
  const isWaitlistMode = WAITLIST_MODE;
  const [theme, setTheme] = useState(() => {
    const sharedTheme = readSharedThemeCookie();
    if (sharedTheme) return sharedTheme;
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === 'dark' || storedTheme === 'light') return storedTheme;
    const cookieTheme = readThemeCookie();
    if (cookieTheme) return cookieTheme;
    return 'light';
  });

  useEffect(() => {
    const activeTheme = isPublicProfileRoute ? 'light' : theme;
    document.documentElement.setAttribute('data-theme', activeTheme);
    document.body.classList.toggle('public-profile-route', isPublicProfileRoute);
    document.body.classList.toggle('account-route', isAccountRoute);
    if (!isPublicProfileRoute) {
      persistTheme(theme);
    }
    return () => {
      document.body.classList.remove('public-profile-route');
      document.body.classList.remove('account-route');
    };
  }, [theme, isPublicProfileRoute, isAccountRoute]);

  useEffect(() => {
    const syncSharedTheme = () => {
      const sharedTheme = readSharedThemeCookie();
      if (sharedTheme) setTheme((current) => current === sharedTheme ? current : sharedTheme);
    };
    window.addEventListener('focus', syncSharedTheme);
    document.addEventListener('visibilitychange', syncSharedTheme);
    return () => {
      window.removeEventListener('focus', syncSharedTheme);
      document.removeEventListener('visibilitychange', syncSharedTheme);
    };
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(() => trackPageView(location.pathname));
    return () => cancelAnimationFrame(frame);
  }, [location.pathname]);

  useEffect(() => {
    initAnalytics();
    const handleConsentGranted = () => {
      initAnalytics();
      trackPageView(window.location.pathname);
    };
    const handleConsentRevoked = () => disableAnalytics();
    window.addEventListener('ac:analytics-consent-granted', handleConsentGranted);
    window.addEventListener('ac:analytics-consent-revoked', handleConsentRevoked);
    return () => {
      window.removeEventListener('ac:analytics-consent-granted', handleConsentGranted);
      window.removeEventListener('ac:analytics-consent-revoked', handleConsentRevoked);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.artificialCareerDesktop?.onNavigate) return undefined;
    const offNavigate = window.artificialCareerDesktop.onNavigate((path) => {
      if (typeof path === 'string' && path.startsWith('/')) {
        navigate(path);
      }
    });
    const offDeepLink = window.artificialCareerDesktop.onDeepLink?.((url) => {
      try {
        const parsed = new URL(url);
        const route = `${parsed.pathname}${parsed.search}`;
        if (route.startsWith('/')) navigate(route);
      } catch {
        // ignore malformed deep links
      }
    });
    return () => {
      offNavigate?.();
      offDeepLink?.();
    };
  }, [navigate]);

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [showOverlayUi, setShowOverlayUi] = useState(false);
  const [isNotFoundRoute, setIsNotFoundRoute] = useState(false);
  const [footerSentinelRef, showFooter] = useNearViewport({ rootMargin: '640px' });
  const isOnline = useOnlineStatus();

  // Allow any page to open the auth modal via a custom event (e.g. AlertDigestPage)
  useEffect(() => {
    const handler = () => setAuthModalOpen(true);
    window.addEventListener('ac:open-auth', handler);
    return () => window.removeEventListener('ac:open-auth', handler);
  }, []);

  // Hero pill → full-width: only toggle body class on pages that actually have a hero section
  useEffect(() => {
    const path = location.pathname.replace(/\/$/, '') || '/';
    if (!HERO_PATHS.has(path)) {
      document.body.classList.remove('hero-scrolled');
      return;
    }
    const onScroll = () => {
      document.body.classList.toggle('hero-scrolled', window.scrollY > 30);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.body.classList.remove('hero-scrolled');
    };
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const revealOverlayUi = () => {
      if (!cancelled) setShowOverlayUi(true);
    };

    if (typeof window !== 'undefined') {
      const events = ['pointerdown', 'keydown', 'scroll', 'focusin'];
      events.forEach((eventName) => {
        window.addEventListener(eventName, revealOverlayUi, { once: true, passive: eventName === 'scroll' });
      });

      return () => {
        cancelled = true;
        events.forEach((eventName) => window.removeEventListener(eventName, revealOverlayUi));
      };
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const openAuthModal = () => setAuthModalOpen(true);

  const withBoundary = (element) => (
    <ErrorBoundary>{element}</ErrorBoundary>
  );

  const renderHomeRoute = ({ key, path, ...homeProps }) => (
    <Route
      key={key}
      path={path}
      element={withBoundary(
        <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
          <Home key={key} {...homeProps} onOpenAuth={openAuthModal} />
        </Suspense>
      )}
    />
  );

  return (
    <CurrencyProvider>
      <>
        <a
          href="#main-content"
          className="skip-nav-link"
          style={{
            position: 'absolute',
            top: '-100%',
            left: 0,
            zIndex: 9999,
            padding: '0.75rem 1.25rem',
            background: 'var(--accent, #6366f1)',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '0 0 8px 0',
            textDecoration: 'none',
            transition: 'top 0.1s',
          }}
          onFocus={e => { e.currentTarget.style.top = '0'; }}
          onBlur={e => { e.currentTarget.style.top = '-100%'; }}
        >
          {t('common.skipToMainContent')}
        </a>
        <ScrollToTop />
        <ToastNotifications />
        {!isOnline && (
          <div
            role="alert"
            style={{
              background: 'var(--accent-tertiary, #7C3AED)',
              color: '#fff',
              textAlign: 'center',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            You are offline. Some features may be unavailable.
          </div>
        )}
        {!isPublicProfileRoute && !isAccountRoute && !isSignupRoute && !isEmbedded && !isDesktopWorkspaceRoute && !isTrainingStudioRoute
          ? <Header theme={theme} toggleTheme={toggleTheme} onOpenAuth={openAuthModal} />
          : null}
        {authModalOpen && (
          <Suspense fallback={null}>
            <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />
          </Suspense>
        )}
        <Suspense fallback={isAccountRoute ? <BrandedAccountLoader /> : <div style={{ minHeight: '60vh' }} />}>
          <Routes>
            {isWaitlistMode ? (
              <>
                {renderHomeRoute({ key: 'waitlist-home', path: '/' })}
                <Route path="/learn-ai" element={withBoundary(<LearnAiPage />)} />
                <Route path="/learn-ai/article/:slug" element={withBoundary(<ArticlePage />)} />
                {!PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/:slug" element={withBoundary(<TrainingPage />)} /> : null}
                {!PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/:slug/dashboard" element={withBoundary(<TrainingBoardPage />)} /> : null}
                {!PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/:slug/:dayId" element={withBoundary(<TrainingPage />)} /> : null}
                {!PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/:slug/:dayId/:chapterId" element={withBoundary(<TrainingPage />)} /> : null}
                {PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/*" element={<Navigate to="/learn-ai" replace />} /> : null}
                <Route path="/about" element={withBoundary(<AboutPage />)} />
                <Route path="/help" element={withBoundary(<HelpPage />)} />
                <Route path="/faq" element={withBoundary(<HelpPage />)} />
                <Route path="/privacy" element={withBoundary(<PrivacyPage />)} />
                <Route path="/terms" element={withBoundary(<TermsPage />)} />
                <Route path="/cookies" element={withBoundary(<CookiePolicyPage />)} />
                <Route path="/ai-transparency" element={withBoundary(<AiTransparencyPage />)} />
                <Route
                  path="*"
                  element={withBoundary(<NotFoundPage onActiveChange={setIsNotFoundRoute} />)}
                />
              </>
            ) : (
              <>
                {renderHomeRoute({ key: 'home', path: '/' })}
                {renderHomeRoute({ key: 'jobs', path: '/jobs' })}
                {CITY_HOME_ROUTES.map(renderHomeRoute)}
                <Route path="/ai-jobs-in-:city" element={withBoundary(<JobCityLanding onOpenAuth={openAuthModal} />)} />
                <Route path="/events-in-:city" element={withBoundary(<EventCityLanding onOpenAuth={openAuthModal} />)} />
                {ROLE_HOME_ROUTES.map(renderHomeRoute)}
                {SPECIALIZED_HOME_ROUTES.map(renderHomeRoute)}
                {COMING_SOON_ROUTES.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={withBoundary(<ComingSoonPage {...route} />)}
                  />
                ))}
                <Route element={<AccountLayout />}>
                  <Route path="/account" element={withBoundary(<AccountShell />)}>
                    <Route index element={<Navigate to={PRODUCTION_PRODUCT_MODE ? 'alerts' : 'overview'} replace />} />
                    {PRODUCTION_PRODUCT_MODE ? (
                      <>
                        <Route path="preferences" element={withBoundary(<ProductionPreferencesSection />)} />
                        <Route path="overview" element={<Navigate to="/account/alerts" replace />} />
                        <Route path="onboarding" element={<Navigate to="/account/alerts" replace />} />
                        <Route path="alerts" element={withBoundary(<AccountJobsSection />)} />
                        <Route path="saved-jobs" element={withBoundary(<AccountSavedJobsSection />)} />
                        <Route path="settings" element={withBoundary(<AccountSettingsSection />)} />
                        <Route path="tools" element={<Navigate to="/account/settings" replace />} />
                        <Route path="*" element={<Navigate to="/account/alerts" replace />} />
                      </>
                    ) : null}
                    {!PRODUCTION_PRODUCT_MODE ? (
                      <>
                    <Route path="onboarding" element={withBoundary(<AccountOnboardingSection />)} />
                    <Route path="overview"    element={withBoundary(<AccountOverviewSection />)} />
                    <Route path="general"     element={<Navigate to="/account/profile" replace />} />
                    <Route path="profile"     element={withBoundary(<AccountSetupSection />)} />
                    <Route path="settings"    element={withBoundary(<AccountSettingsSection />)} />
                    <Route path="swipe"       element={withBoundary(<AccountSwipeSection />)} />
                    <Route path="alerts"      element={withBoundary(<AccountJobsSection />)} />
                    <Route path="jobs"        element={<Navigate to="/account/alerts" replace />} />
                    <Route path="events"      element={<Navigate to="/account/alerts?tab=events" replace />} />
                    <Route path="tools"       element={<Navigate to="/account/profile" replace />} />
                    <Route path="inbox"       element={withBoundary(<AccountInboxSection />)} />
                    <Route path="cv"          element={withBoundary(<AccountCvSection />)} />
                    <Route path="agent/:conversationId?" element={withBoundary(<AccountAgentSection />)} />
                    <Route path="smart-search" element={withBoundary(<AccountSmartSearchSection />)} />
                    <Route path="tracking"    element={withBoundary(<AccountTrackingSection />)} />
                    <Route path="board"       element={withBoundary(<AccountBoardSection />)} />
                    <Route path="billing"     element={withBoundary(<AccountBillingSection />)} />
                    {!PRODUCTION_PRODUCT_MODE ? <Route path="client-training/ned-opportunity" element={withBoundary(<ClientNedTrainingPage />)} /> : null}
                    {!PRODUCTION_PRODUCT_MODE ? <Route path="client-training/ned-opportunity/:dayId/:chapterId" element={withBoundary(<ClientNedTrainingPage />)} /> : null}
                    <Route path="saved-jobs"  element={<Navigate to="/account/tracking?scope=saved" replace />} />
                      </>
                    ) : null}
                  </Route>
                </Route>
                <Route path="/job/:id" element={withBoundary(<JobDetail onOpenAuth={openAuthModal} />)} />
                <Route path="/company/:companyId/salaries" element={withBoundary(<CompanySalaryPage />)} />
                <Route path="/company/:companyId" element={withBoundary(<CompanyPage />)} />
                <Route path="/companies" element={withBoundary(<CompaniesPage onOpenAuth={openAuthModal} />)} />
                <Route path="/salaries" element={withBoundary(<SalaryPage />)} />
                <Route path="/observatory" element={withBoundary(<ObservatoryPage />)} />
                <Route path="/observatory/:month" element={withBoundary(<ObservatoryPage />)} />
                <Route path="/observatory/:editionCountry/:month" element={withBoundary(<ObservatoryPage />)} />
                {SALARY_DETAIL_PATHS.map((path) => (
                  <Route key={path} path={path} element={withBoundary(<SalaryDetail />)} />
                ))}
                <Route path="/learn-ai" element={withBoundary(<LearnAiPage />)} />
                <Route path="/about" element={withBoundary(<AboutPage />)} />
                <Route path="/contact" element={withBoundary(<ContactPage />)} />
                <Route path="/help" element={withBoundary(<HelpPage />)} />
                <Route path="/faq" element={withBoundary(<HelpPage />)} />
                <Route path="/learn-ai/article/:slug" element={withBoundary(<ArticlePage />)} />
                {!PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/:slug" element={withBoundary(<TrainingPage />)} /> : null}
                {!PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/:slug/dashboard" element={withBoundary(<TrainingBoardPage />)} /> : null}
                {!PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/:slug/:dayId" element={withBoundary(<TrainingPage />)} /> : null}
                {!PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/:slug/:dayId/:chapterId" element={withBoundary(<TrainingPage />)} /> : null}
                {PRODUCTION_PRODUCT_MODE ? <Route path="/learn-ai/training/*" element={<Navigate to="/learn-ai" replace />} /> : null}
                <Route path="/ai-tools" element={withBoundary(<ToolsPage onOpenAuth={openAuthModal} />)} />
                <Route path="/ai-tools/:slug" element={withBoundary(<ToolDetailPage onOpenAuth={openAuthModal} />)} />
                <Route path="/privacy" element={withBoundary(<PrivacyPage />)} />
                <Route path="/terms" element={withBoundary(<TermsPage />)} />
                <Route path="/cookies" element={withBoundary(<CookiePolicyPage />)} />
                <Route path="/ai-transparency" element={withBoundary(<AiTransparencyPage />)} />
                <Route path="/ads" element={withBoundary(<Advertise />)} />
                <Route path="/auth/callback" element={withBoundary(<AuthCallbackPage />)} />
                <Route path="/signup" element={withBoundary(<SignupPage />)} />
                <Route path="/events" element={withBoundary(<EventsPage onOpenAuth={openAuthModal} />)} />
                <Route path="/events/:id" element={withBoundary(<EventDetail />)} />
                <Route path="/alerts/digest" element={withBoundary(<AlertDigestPage />)} />
                <Route path="/pricing" element={PRODUCTION_PRODUCT_MODE ? <Navigate to="/" replace /> : withBoundary(<PricingPage onOpenAuth={openAuthModal} />)} />
                <Route path="/my/:username" element={withBoundary(<PublicProfilePage />)} />
                <Route path="/my/:username/cv" element={withBoundary(<PublicProfileCvPage />)} />
                <Route path="/desktop/workspace" element={PRODUCTION_PRODUCT_MODE ? <Navigate to="/" replace /> : withBoundary(<DesktopWorkspacePage />)} />
                <Route
                  path="*"
                  element={withBoundary(<NotFoundPage onActiveChange={setIsNotFoundRoute} />)}
                />
              </>
            )}
          </Routes>
        </Suspense>
        {!isDesktopWorkspaceRoute && !isTrainingStudioRoute && !isSignupRoute ? (
          <div ref={footerSentinelRef} aria-hidden="true" style={{ width: 1, height: 1 }} />
        ) : null}
        {/* CookieBanner manages its own 1500ms delay internally; render it unconditionally
            so consent prompt appears even when the user hasn't interacted yet */}
        {!isDesktopWorkspaceRoute && !isSignupRoute ? (
          <Suspense fallback={null}>
            <CookieBanner />
          </Suspense>
        ) : null}
        {!isDesktopWorkspaceRoute && !isSignupRoute && (showFooter || showOverlayUi) && (
          <Suspense fallback={null}>
            {showOverlayUi ? <LanguageDetectBanner /> : null}
            {!isNotFoundRoute && !isAccountRoute && !isTrainingStudioRoute && !isWaitlistMode && !isEmbedded && showFooter
              ? <Footer />
              : null}
          </Suspense>
        )}
      </>
    </CurrencyProvider>
  )
}

export default App
